<template>
  <div class="localization-page" id="loading-container">
    <section class="content-header">
      <h1><i :class="routeIcon"></i> Localization</h1>
    </section>
    <section class="content">
      <div class="container-fluid">
        <div class="d-flex flex-wrap align-items-center mb-3 localization-toolbar">
          <button class="btn btn-success btn-sm" :disabled="busy" @click="openKey()"><i class="fa fa-plus"></i> New key</button>
          <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="openGroup()"><i class="fa fa-folder-plus"></i> New group</button>
          <button class="btn btn-outline-primary btn-sm" :disabled="busy || !releaseId || !cdn.enabled" @click="deploy"><i class="fa fa-cloud-upload-alt"></i> Deploy</button>
          <span class="small" role="status">Ready to deploy: {{ readyCount }}</span>
          <button class="btn btn-outline-secondary btn-sm" :disabled="busy" title="Refresh" aria-label="Refresh" @click="loadData()"><i class="fa fa-sync"></i></button>
          <button class="btn btn-outline-secondary btn-sm" title="Import" aria-label="Import" @click="$refs.importModal.toggle()"><i class="fa fa-download"></i></button>
          <button class="btn btn-outline-secondary btn-sm" title="Export" aria-label="Export" @click="$refs.exportModal.toggle()"><i class="fa fa-upload"></i></button>
          <button class="btn btn-outline-secondary btn-sm" title="Languages" aria-label="Languages" @click="$refs.statsModal.toggle()"><i class="fa fa-cog"></i></button>
          <button class="btn btn-outline-secondary btn-sm" @click="$refs.helpModal.show()">Help / tournaments</button>
          <span class="small" :class="cdnStatusClass" role="status">CDN: {{ cdnStatusLabel }}</span>
        </div>
        <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
        <div v-if="notice" class="alert alert-success" role="status">{{ notice }}</div>
        <div class="row">
          <div class="col-lg-4 mb-3">
            <label class="sr-only" for="localization-search">Search</label>
            <input id="localization-search" v-model="search" class="form-control form-control-sm mb-2" placeholder="Search key or text" type="search">
            <div class="d-flex localization-toolbar mb-2">
              <select v-model="groupFilter" aria-label="Group filter" class="form-control form-control-sm">
                <option value="">All groups</option>
                <option v-for="group in activeGroups" :key="group.id" :value="group.id">{{ group.name }}</option>
              </select>
              <select v-model="statusFilter" aria-label="Status filter" class="form-control form-control-sm">
                <option value="">All statuses</option>
                <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
              </select>
              <button :disabled="!groupFilter || busy" class="btn btn-outline-secondary btn-sm" title="Rename group" aria-label="Rename group" @click="openGroup(activeGroups.find(g => g.id === groupFilter))"><i class="fa fa-pen"></i></button>
              <button :disabled="!groupFilter || busy" class="btn btn-outline-danger btn-sm" title="Delete group" aria-label="Delete group" @click="confirmGroupDelete"><i class="fa fa-trash"></i></button>
            </div>
            <div class="localization-key-list">
              <button v-for="term in pageTerms" :key="term.id" class="localization-key" :class="{selected: selectedId === term.id}" @click="select(term)">
                <span>{{ term.key }}</span>
                <small :class="statusClass(term.publication)">{{ term.operation === 'delete' ? 'deletion / ' : '' }}{{ term.publication }}</small>
              </button>
              <p v-if="!filteredTerms.length" class="text-muted p-3">{{ loading ? 'Loading...' : 'No matching keys.' }}</p>
            </div>
            <b-pagination v-model="page" :total-rows="filteredTerms.length" :per-page="pageSize" size="sm" class="mt-2" />
          </div>
          <div class="col-lg-8">
            <template v-if="selected">
              <div class="d-flex align-items-start justify-content-between mb-2">
                <div class="localization-heading">
                  <h2>{{ selected.key }}</h2>
                  <small>Revision {{ selected.revision }} / {{ selected.publication }}</small>
                </div>
                <div class="d-flex localization-toolbar">
                  <button class="btn btn-outline-secondary btn-sm" :disabled="busy || selected.operation === 'delete'" title="Rename key" aria-label="Rename key" @click="openKey(selected)"><i class="fa fa-pen"></i></button>
                  <button class="btn btn-outline-danger btn-sm" :disabled="busy || selected.operation === 'delete'" title="Delete key" aria-label="Delete key" @click="confirmDelete"><i class="fa fa-trash"></i></button>
                </div>
              </div>
              <div v-for="language in orderedLanguages" :key="language.id" class="localization-translation py-3">
                <form @submit.prevent="apply(language)">
                  <div class="d-flex justify-content-between mb-1">
                    <label :for="'translation-' + language.id">{{ language.name }} ({{ language.code }})</label>
                    <small :class="statusClass(job(language).status)">{{ job(language).status || 'no source' }}</small>
                  </div>
                  <textarea :id="'translation-' + language.id" v-model="drafts[language.id]" class="form-control form-control-sm" rows="3" required :disabled="busy || selected.operation === 'delete'"></textarea>
                  <div class="d-flex flex-wrap align-items-center justify-content-between mt-2 localization-toolbar">
                    <small class="text-muted">Attempts: {{ job(language).attempts || 0 }}<span v-if="job(language).lastError" class="text-danger"> / {{ job(language).lastError }}</span></small>
                    <div class="d-flex localization-toolbar">
                      <button v-if="['failed', 'error', 'retry'].includes(job(language).status)" type="button" class="btn btn-outline-warning btn-sm" :disabled="busy" @click="retry(language)"><i class="fa fa-redo"></i> Retry</button>
                      <button type="submit" class="btn btn-success btn-sm" :disabled="busy || selected.operation === 'delete'"><i class="fa fa-check"></i> Apply</button>
                    </div>
                  </div>
                  <details v-if="selected.published && selected.published.texts[language.code] !== undefined" class="mt-2">
                    <summary class="text-muted">Published revision {{ selected.published.revision }}</summary>
                    <pre class="localization-published">{{ selected.published.texts[language.code] }}</pre>
                  </details>
                </form>
              </div>
            </template>
            <p v-else class="text-muted">No key selected.</p>
          </div>
        </div>
      </div>
    </section>
    <b-modal v-model="keyModal" :title="keyForm.id ? 'Rename key' : 'New key'" hide-footer :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy">
      <form @submit.prevent="saveKey">
        <fieldset :disabled="busy">
          <div v-if="formError" class="alert alert-danger" role="alert">{{ formError }}</div>
          <div class="form-group"><label for="key-group">Group</label><select id="key-group" v-model="keyForm.groupId" required class="form-control"><option value="" disabled>Select group</option><option v-for="group in activeGroups" :key="group.id" :value="group.id">{{ group.name }}</option></select></div>
          <div class="form-group"><label for="key-name">Key</label><input id="key-name" v-model="keyForm.name" required pattern="[A-Za-z0-9_\+\(\)\/](?:[A-Za-z0-9_. \+\(\)\/\-]{0,189}[A-Za-z0-9_.\+\(\)\/\-])?" maxlength="191" class="form-control"></div>
          <div class="form-group"><label for="key-source">English</label><textarea id="key-source" v-model="keyForm.sourceText" required rows="5" class="form-control"></textarea></div>
          <div class="d-flex justify-content-end localization-toolbar"><button type="button" class="btn btn-light" @click="keyModal = false">Cancel</button><button type="submit" class="btn btn-success"><i class="fa fa-check"></i> Apply</button></div>
        </fieldset>
      </form>
    </b-modal>
    <b-modal v-model="groupModal" :title="groupForm.id ? 'Rename group' : 'New group'" hide-footer :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy">
      <form @submit.prevent="saveGroup">
        <fieldset :disabled="busy">
          <div v-if="formError" class="alert alert-danger" role="alert">{{ formError }}</div>
          <div class="form-group"><label for="group-name">Name</label><input id="group-name" v-model="groupForm.name" required pattern="[A-Za-z0-9_][A-Za-z0-9_\-]{0,63}" maxlength="64" class="form-control"></div>
          <button type="submit" class="btn btn-success float-right"><i class="fa fa-check"></i> Apply</button>
        </fieldset>
      </form>
    </b-modal>
    <b-modal v-model="deleteModal" title="Confirm deletion" hide-footer :no-close-on-backdrop="busy" :no-close-on-esc="busy" :hide-header-close="busy">
      <p class="localization-heading">{{ deletion.name }}</p>
      <div class="d-flex justify-content-end localization-toolbar"><button class="btn btn-light" :disabled="busy" @click="deleteModal = false">Cancel</button><button class="btn btn-danger" :disabled="busy" @click="remove"><i class="fa fa-trash"></i> Delete in all languages</button></div>
    </b-modal>
    <StatsModal ref="statsModal" :disabled-codes="['en']" :languages="languages" :translations="translations" :terms="terms" :hidden-codes="hiddenCodes" @hideLanguage="hideLanguage" @showLanguage="showLanguage" @languageRemoved="loadData()" @languageChanged="loadData()" @languageCreated="loadData()" />
    <ExportModal ref="exportModal" :languages="languages" :release-id="releaseId" />
    <ImportModal ref="importModal" :languages="languages" @onComplete="loadData()" />
    <HelpAndTournamentsModal ref="helpModal" :languages="languages" />
  </div>
</template>

<script>
import {getRouteIcon, ROUTE_LOCALIZATION} from "../../utils/routerIcons";
import StatsModal from "../../components/Localization/StatsModal";
import ExportModal from "../../components/Localization/ExportModal";
import ImportModal from "../../components/Localization/ImportModal";
import HelpAndTournamentsModal from "../../components/Localization/HelpAndTournamentsModal";

export default {
  components: {StatsModal, ExportModal, ImportModal, HelpAndTournamentsModal},
  data() {
    return {
      groups: [], terms: [], languages: [], hiddenCodes: [], generation: 0, releaseId: null, selectedId: null, drafts: {}, baseDrafts: {}, editVersion: null,
      search: '', groupFilter: '', statusFilter: '', page: 1, pageSize: 30, busy: false, loading: false,
      error: '', notice: '', formError: '', keyModal: false, groupModal: false, deleteModal: false,
      keyForm: {}, groupForm: {}, deletion: {}, timer: null,
      cdn: {enabled: false}, cdnStatus: 'checking', cdnChecking: false, cdnCheckedAt: 0, cdnCheckedRelease: null, cdnRequest: 0,
      statuses: ['staging', 'ready', 'published', 'pending', 'backfill_required', 'in_progress', 'translated', 'retry', 'failed', 'deleted']
    };
  },
  computed: {
    routeIcon: () => getRouteIcon(ROUTE_LOCALIZATION),
    cdnStatusLabel() { return {checking: 'checking', disabled: 'disabled', current: 'connected', outdated: 'outdated', unavailable: 'unavailable'}[this.cdnStatus] || 'unavailable'; },
    cdnStatusClass() { return this.cdnStatus === 'current' ? 'text-success' : ['outdated', 'unavailable'].includes(this.cdnStatus) ? 'text-danger' : 'text-muted'; },
    activeGroups() { return this.groups.filter(g => !g.deleted).sort((a, b) => a.name.localeCompare(b.name)); },
    selected() { return this.terms.find(t => t.id === this.selectedId); },
    orderedLanguages() { return this.languages.filter(l => l.code === 'en' || !this.hiddenCodes.includes(l.code)).sort((a, b) => a.code === 'en' ? -1 : b.code === 'en' ? 1 : a.code.localeCompare(b.code)); },
    filteredTerms() {
      const search = this.search.toLowerCase();
      return this.terms.filter(t => (!this.groupFilter || t.groupId === this.groupFilter)
        && (t.key.toLowerCase().includes(search) || (t.sourceText || '').toLowerCase().includes(search)
          || t.jobs.some(j => (j.result || '').toLowerCase().includes(search)))
        && this.matchesStatus(t))
        .sort((a, b) => a.key.localeCompare(b.key));
    },
    pageTerms() { return this.filteredTerms.slice((this.page - 1) * this.pageSize, this.page * this.pageSize); },
    readyCount() { return this.terms.filter(t => t.publication === 'ready').length; },
    dirty() { return JSON.stringify(this.drafts) !== JSON.stringify(this.baseDrafts); },
    translations() {
      return this.terms.flatMap(t => t.jobs.filter(j => j.result !== null).map(j => ({id: j.id, termId: t.id, languageId: j.languageId, text: j.result})));
    }
  },
  watch: {
    search() { this.page = 1; }, groupFilter() { this.page = 1; }, statusFilter() { this.page = 1; }
  },
  methods: {
    hideLanguage(language) { if (language.code !== 'en' && !this.hiddenCodes.includes(language.code)) this.hiddenCodes.push(language.code); },
    showLanguage(language) { this.hiddenCodes = this.hiddenCodes.filter(code => code !== language.code); },
    message(error) { return error.response && error.response.data && error.response.data.message || 'Localization request failed.'; },
    statusClass(status) { return ['failed', 'error'].includes(status) ? 'text-danger' : ['ready', 'published', 'translated'].includes(status) ? 'text-success' : 'text-muted'; },
    job(language) { return this.selected && this.selected.jobs.find(j => j.languageId === language.id) || {}; },
    matchesStatus(term) {
      if (!this.statusFilter) return true;
      if (this.statusFilter === 'deleted') return term.operation === 'delete' || term.status === 'deleted';
      if (['staging', 'ready', 'published'].includes(this.statusFilter)) return term.publication === this.statusFilter;
      if (term.publication === 'published' || term.operation === 'delete') return false;
      return term.status === this.statusFilter || term.jobs.some(job => job.status === this.statusFilter);
    },
    select(term) {
      this.selectedId = term.id;
      this.drafts = {};
      this.languages.forEach(lang => {
        const job = term.jobs.find(j => j.languageId === lang.id);
        this.$set(this.drafts, lang.id, lang.code === 'en' ? term.sourceText || '' : job && job.result !== null ? job.result : term.published && term.published.texts[lang.code] || '');
      });
      this.baseDrafts = {...this.drafts};
      this.editVersion = term.editVersion;
    },
    async loadData(poll = false) {
      if (this.loading || (poll && (this.busy || this.keyModal || this.groupModal || this.deleteModal))) return;
      this.loading = true;
      try {
        const {data} = await this.$http.get('/api/v1/localization/revisions');
        const previousSelected = this.selectedId;
        const wasDirty = this.dirty;
        this.groups = data.groups; this.terms = data.terms; this.languages = data.languages; this.generation = data.generation; this.releaseId = data.releaseId;
        this.cdn = data.cdn || {enabled: false};
        if (this.selected && !wasDirty) this.select(this.selected);
        else if (this.selected && this.selected.id === previousSelected) {
          this.languages.forEach(language => {
            if (this.drafts[language.id] !== this.baseDrafts[language.id]) return;
            const value = language.code === 'en' ? this.selected.sourceText || ''
              : this.job(language).result !== null && this.job(language).result !== undefined ? this.job(language).result
                : this.selected.published && this.selected.published.texts[language.code] || '';
            this.$set(this.drafts, language.id, value);
            this.$set(this.baseDrafts, language.id, value);
          });
        }
        else if (!this.selected && this.terms.length) this.select(this.terms[0]);
        if (!poll) this.error = '';
        this.checkCdn(!poll);
      } catch (error) { this.error = this.message(error); }
      finally { this.loading = false; }
    },
    async checkCdn(force = false) {
      if (!this.cdn.enabled) {
        this.cdnRequest++;
        this.cdnStatus = 'disabled';
        return;
      }
      if (this.cdnChecking || (!force && this.cdnCheckedRelease === this.releaseId && Date.now() - this.cdnCheckedAt < 60000)) return;
      const request = ++this.cdnRequest;
      const release = this.releaseId;
      this.cdnChecking = true;
      this.cdnStatus = 'checking';
      try {
        const {data} = await this.$http.get('/api/v1/localization/delivery');
        if (request !== this.cdnRequest || !this.cdn.enabled) return;
        this.cdnStatus = data.status === 'current' && (!data.sha256Verified || data.releaseId !== this.releaseId || data.cdnReleaseId !== data.releaseId)
          ? 'outdated' : ['current', 'outdated', 'unavailable', 'disabled'].includes(data.status) ? data.status : 'unavailable';
        this.cdnCheckedRelease = release;
      } catch (error) {
        if (request === this.cdnRequest) this.cdnStatus = 'unavailable';
      } finally {
        this.cdnChecking = false;
        this.cdnCheckedAt = Date.now();
      }
    },
    async action(operation, success) {
      if (this.busy) return false;
      this.busy = true; this.error = ''; this.formError = ''; this.notice = '';
      try {
        await operation();
        this.notice = success;
        return true;
      } catch (error) {
        this.error = this.message(error); this.formError = this.error;
        return false;
      } finally { this.busy = false; }
    },
    async apply(language) {
      const ok = await this.action(async () => {
        const {data} = await this.$http.post('/api/v1/localization/translations', {
          termId: this.selected.id, languageId: language.id, text: this.drafts[language.id], expectedVersion: this.editVersion
        });
        this.editVersion = data.editVersion;
      }, 'Saved to staging.');
      if (ok) {
        this.$set(this.baseDrafts, language.id, this.drafts[language.id]);
        await this.loadData();
      }
    },
    openKey(term) {
      this.formError = '';
      this.keyForm = term ? {id: term.id, groupId: term.groupId, name: term.name, sourceText: term.sourceText || '', expectedVersion: term.editVersion}
        : {id: null, groupId: this.groupFilter || (this.activeGroups[0] || {}).id || '', name: '', sourceText: ''};
      this.keyModal = true;
    },
    async saveKey() {
      const form = {...this.keyForm};
      const ok = await this.action(async () => {
        const {data} = await this.$http({url: '/api/v1/localization/terms' + (form.id ? '/' + form.id : ''), method: form.id ? 'PUT' : 'POST', data: form});
        this.selectedId = data.id;
      }, 'Key revision saved to staging.');
      if (ok) { this.keyModal = false; this.drafts = {}; this.baseDrafts = {}; await this.loadData(); }
    },
    openGroup(group) {
      this.formError = '';
      this.groupForm = group ? {...group, expectedVersion: this.generation} : {id: null, name: ''};
      this.groupModal = true;
    },
    async saveGroup() {
      const form = {...this.groupForm};
      const ok = await this.action(() => this.$http({url: '/api/v1/localization/groups' + (form.id ? '/' + form.id : ''), method: form.id ? 'PUT' : 'POST', data: form}), 'Group saved.');
      if (ok) { this.groupModal = false; await this.loadData(); }
    },
    confirmDelete() {
      this.deletion = {name: this.selected.key, path: 'terms/' + this.selected.id, expectedVersion: this.selected.editVersion};
      this.deleteModal = true;
    },
    confirmGroupDelete() {
      const group = this.activeGroups.find(g => g.id === this.groupFilter);
      this.deletion = {name: group.name, path: 'groups/' + group.id, expectedVersion: this.generation};
      this.deleteModal = true;
    },
    async remove() {
      const ok = await this.action(() => this.$http.delete('/api/v1/localization/' + this.deletion.path, {params: {expectedVersion: this.deletion.expectedVersion}}), 'Deletion staged for all languages.');
      if (ok) { this.deleteModal = false; this.groupFilter = ''; this.drafts = {}; this.baseDrafts = {}; await this.loadData(); }
    },
    async retry(language) {
      const ok = await this.action(() => this.$http.post('/api/v1/localization/revisions/' + this.selected.id + '/retry', null, {params: {languageId: language.id}}), 'Retry queued.');
      if (ok) await this.loadData();
    },
      async deploy() {
        const ok = await this.action(() => this.$http.post('/api/v1/localization/deploy'), 'Ready revisions deployed.');
        if (ok) await this.loadData();
      }
  },
  mounted() {
    this.loadData();
    this.timer = setInterval(() => this.loadData(true), 10000);
  },
  beforeDestroy() { clearInterval(this.timer); this.cdnRequest++; }
};
</script>

<style scoped>
.localization-page { letter-spacing: 0; }
.localization-toolbar { gap: 6px; }
.localization-toolbar .form-control { min-width: 0; flex: 1; }
.localization-key-list { max-height: 65vh; overflow: auto; border-block: 1px solid #dee2e6; }
.localization-key { width: 100%; border: 0; border-bottom: 1px solid #e9ecef; background: transparent; text-align: left; padding: 9px 10px; display: flex; flex-direction: column; overflow-wrap: anywhere; }
.localization-key:hover, .localization-key.selected { background: #eaf3ed; }
.localization-key.selected { box-shadow: inset 3px 0 #218838; }
.localization-heading { min-width: 0; overflow-wrap: anywhere; }
.localization-heading h2 { font-size: 20px; margin: 0; }
.localization-translation { border-bottom: 1px solid #dee2e6; }
.localization-published { white-space: pre-wrap; overflow-wrap: anywhere; font-size: 13px; padding: 8px 0; }
.localization-page textarea { resize: vertical; min-height: 80px; }
</style>
